/**
 * Organization admin user lookup using React Relay.
 *
 * Mirrors `useAdminUserRelay.ts` but for organization roles: resolves an admin's
 * GraphQL `id` (needed as `adminId` for role grants) plus the set of currently
 * active organization role rows (needed to soft-delete on revoke), keyed by role
 * name.
 *
 * Note: Run `npm run relay` after editing GraphQL queries to regenerate type
 * artifacts under __generated__.
 */

import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';

const OrgAdminUserByEmailQuery = graphql`
  query useOrgAdminUserRelayByEmailQuery($email: String!, $organizationId: ID!) {
    listAdminUsers(filter: { email: { eq: $email } }, first: 1) {
      edges {
        node {
          id
          email
          firstName
          lastName
          adminOrganizationRoles(filter: { organizationId: { eq: $organizationId } }) {
            id
            roleId
            removedOn
            role {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export interface OrgAdminUserLookup {
  /** GraphQL global id of the admin (use as `adminId` for role mutations). */
  adminId: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Active (not soft-deleted) organization role row ids, keyed by role name. */
  roleRowsByName: Record<string, string>;
}

/**
 * Look up an admin by email within an organization. Returns the admin's GraphQL
 * id, name, and a map of active role name -> AdminOrganizationRole row id.
 * Returns null if not found.
 */
export async function fetchOrgAdminUserByEmail(
  email: string,
  organizationId: string
): Promise<OrgAdminUserLookup | null> {
  const result = await fetchQuery<any>(relayEnvironment, OrgAdminUserByEmailQuery, {
    email,
    organizationId,
  }).toPromise();

  const node = result?.listAdminUsers?.edges?.[0]?.node;
  if (!node) return null;

  const roleRowsByName: Record<string, string> = {};
  for (const row of node.adminOrganizationRoles ?? []) {
    // Skip already soft-deleted rows; only track active grants.
    if (row?.removedOn) continue;
    const name = row?.role?.name;
    if (name && row?.id) {
      roleRowsByName[name] = row.id;
    }
  }

  return {
    adminId: node.id,
    email: node.email,
    firstName: node.firstName,
    lastName: node.lastName,
    roleRowsByName,
  };
}
